/**
 * @file The HYPERVISOR source contribution for the Dashboard — an
 *       honest not-connected tile until a hypervisor connection lands,
 *       and incident Needs-you items once it does. Pure; unit-tested.
 * @module @labcraft/admin/ui/hypervisor/contribution
 */

import type {
  NeedsYouItem,
  SourceContributionData,
} from '../sources/contract.ts'
import type { HypervisorListResponse } from '../../shared/hypervisor_routes.ts'
import { HypervisorView } from './view.ts'

/** Builds the HYPERVISOR contribution from the machine-list response. */
export class HypervisorContribution {
  /**
   * @param response - The `GET /api/hypervisor` response.
   * @returns The HYPERVISOR Needs-you items + Sources tile.
   */
  static build(response: HypervisorListResponse): SourceContributionData {
    if (!response.connected) {
      return {
        items: [],
        tile: {
          source: 'HYPERVISOR',
          label: 'Hypervisor',
          health: 'muted',
          state: 'not-connected',
          needsYouCount: 0,
          stat: 'sandbox fleet — not connected yet',
          route: '/hypervisors',
        },
      }
    }
    const incidents = response.machines.filter(HypervisorView.isIncident)
    const { running, total } = HypervisorView.capacity(response.machines)
    const items: NeedsYouItem[] = incidents.map(m => ({
      id: `hypervisor-${m.id}`,
      source: 'HYPERVISOR',
      severity: 'incident',
      title: `Sandbox ${m.name} stuck ${m.state}`,
      sub: `${m.hostIp} · needs a force-reap`,
      route: '/hypervisors',
      posture: 'error',
    }))
    return {
      items,
      tile: {
        source: 'HYPERVISOR',
        label: 'Hypervisor',
        health: 'live',
        state: incidents.length > 0 ? 'needs-you' : 'ok',
        needsYouCount: incidents.length,
        stat: `${running} running / ${total}`,
        route: '/hypervisors',
      },
    }
  }
}
